import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import IzinTalepFormu from '../components/forms/IzinTalepFormu';
import MasrafBildirimFormu from '../components/forms/MasrafBildirimFormu';

const HRPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingApplication, setRejectingApplication] = useState(null);
  const { toasts, addToast, removeToast } = useToast();
  const [hrData, setHrData] = useState({
    stats: {
      totalEmployees: 0,
      activeLeaveRequests: 0,
      pendingApplications: 0,
      newEmployeesThisMonth: 0
    },
    recentApplications: [],
    recentEmployees: []
  });

  useEffect(() => {
    fetchHRData();
  }, []);

  const fetchHRData = async () => {
    try {
      setLoading(true);
      
      console.log('HR verileri yükleniyor...');
      
      // Token kontrolü
      const token = localStorage.getItem('token');
      console.log('Token var mı:', !!token);
      
      // Paralel olarak tüm HR verilerini çek
      const [statsResponse, applicationsResponse, employeesResponse] = await Promise.all([
        api.getHRStats(),
        api.getRecentApplications(),
        api.getRecentNewEmployees()
      ]);

      setHrData({
        stats: statsResponse.data,
        recentApplications: applicationsResponse.data || [],
        recentEmployees: employeesResponse.data || []
      });

    } catch (error) {
      console.error('HR verileri yüklenirken hata:', error);
      
      // Fallback veriler
      setHrData({
        stats: {
          totalEmployees: 0,
          activeLeaveRequests: 0,
          pendingApplications: 0,
          newEmployeesThisMonth: 0
        },
        recentApplications: [],
        recentEmployees: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Beklemede': 'bg-warning',
      'Onaylandı': 'bg-success',
      'Reddedildi': 'bg-danger'
    };
    return statusColors[status] || 'bg-secondary';
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setShowApplicationModal(true);
  };

  const handleCloseModal = () => {
    setShowApplicationModal(false);
    setSelectedApplication(null);
  };

  const handleApproveApplication = async () => {
    if (!selectedApplication) return;

    try {
      const approverId = localStorage.getItem('userId'); // Mevcut kullanıcı ID'si
      await api.approveApplication(selectedApplication.id, selectedApplication.category, approverId);
      
      // Başarı toast'ı
      addToast(
        `${selectedApplication.employee} adlı çalışanın başvurusu başarıyla onaylandı.`,
        'success',
        5000
      );
      
      // Modal'ı kapat ve verileri yenile
      handleCloseModal();
      fetchHRData();
    } catch (error) {
      console.error('Başvuru onaylanırken hata:', error);
      addToast(
        'Başvuru onaylanırken bir hata oluştu. Lütfen tekrar deneyin.',
        'error',
        5000
      );
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication) return;

    setRejectingApplication(selectedApplication);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingApplication || !rejectReason.trim()) {
      addToast(
        'Lütfen red sebebini giriniz.',
        'warning',
        3000
      );
      return;
    }

    try {
      const approverId = localStorage.getItem('userId');
      await api.rejectApplication(rejectingApplication.id, rejectingApplication.category, approverId, rejectReason);
      
      // Başarı toast'ı
      addToast(
        `${rejectingApplication.employee} adlı çalışanın başvurusu reddedildi.`,
        'warning',
        5000
      );
      
      // Modal'ları kapat ve verileri yenile
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingApplication(null);
      if (showApplicationModal) {
        handleCloseModal();
      }
      fetchHRData();
    } catch (error) {
      console.error('Başvuru reddedilirken hata:', error);
      addToast(
        'Başvuru reddedilirken bir hata oluştu. Lütfen tekrar deneyin.',
        'error',
        5000
      );
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setRejectingApplication(null);
  };

  const handleApproveFromTable = async (application) => {
    try {
      const approverId = localStorage.getItem('userId');
      await api.approveApplication(application.id, application.category, approverId);
      
      addToast(
        `${application.employee} adlı çalışanın başvurusu başarıyla onaylandı.`,
        'success',
        5000
      );
      fetchHRData();
    } catch (error) {
      console.error('Başvuru onaylanırken hata:', error);
      addToast(
        'Başvuru onaylanırken bir hata oluştu. Lütfen tekrar deneyin.',
        'error',
        5000
      );
    }
  };

  const handleRejectFromTable = async (application) => {
    setRejectingApplication(application);
    setShowRejectModal(true);
  };

  if (loading) {
    return (
      <div className="modern-hr-page">
        <div className="container-fluid">
          <div className="modern-hr-header">
            <h1>İnsan Kaynakları</h1>
            <p>Çalışan işlemleri ve İK hizmetleri</p>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
              <h5 className="text-muted">HR verileri yükleniyor...</h5>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <h1>İnsan Kaynakları</h1>
          <p>Çalışan işlemleri ve İK hizmetleri</p>
        </div>

        {/* Tab Navigation */}
        <div className="modern-hr-tabs">
          <div className="d-flex justify-content-center gap-2">
            <button 
              className={`modern-hr-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <i className="bx bx-dashboard"></i>
              İK Dashboard
            </button>
            <button 
              className={`modern-hr-tab ${activeTab === 'izin-talep' ? 'active' : ''}`}
              onClick={() => setActiveTab('izin-talep')}
            >
              <i className="bx bx-calendar"></i>
              İzin Talep Formu
            </button>
                    <button
                      className={`modern-hr-tab ${activeTab === 'masraf-bildirim' ? 'active' : ''}`}
                      onClick={() => setActiveTab('masraf-bildirim')}
                    >
                      <i className="bx bx-receipt"></i>
                      Masraf Bildirim Formu
                    </button>
          </div>
        </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          {/* HR Stats */}
          <div className="modern-hr-stats">
            <div className="modern-hr-stat-card">
              <div className="modern-hr-stat-icon primary">
                <i className="bx bxs-user"></i>
              </div>
              <div className="modern-hr-stat-number">{hrData.stats.totalEmployees}</div>
              <div className="modern-hr-stat-label">Toplam Çalışan</div>
              <div className="modern-hr-stat-change positive">+3%</div>
            </div>
            
            <div className="modern-hr-stat-card">
              <div className="modern-hr-stat-icon success">
                <i className="bx bx-calendar-minus"></i>
              </div>
              <div className="modern-hr-stat-number">{hrData.stats.activeLeaveRequests}</div>
              <div className="modern-hr-stat-label">Aktif İzinler</div>
              <div className="modern-hr-stat-change positive">+12%</div>
            </div>
            
            <div className="modern-hr-stat-card">
              <div className="modern-hr-stat-icon warning">
                <i className="bx bx-time"></i>
              </div>
              <div className="modern-hr-stat-number">{hrData.stats.pendingApplications}</div>
              <div className="modern-hr-stat-label">Bekleyen Başvurular</div>
              <div className="modern-hr-stat-change negative">-5%</div>
            </div>
            
            <div className="modern-hr-stat-card">
              <div className="modern-hr-stat-icon info">
                <i className="bx bx-user-plus"></i>
              </div>
              <div className="modern-hr-stat-number">{hrData.stats.newEmployeesThisMonth}</div>
              <div className="modern-hr-stat-label">Bu Ay İşe Başlayanlar</div>
              <div className="modern-hr-stat-change positive">+8%</div>
            </div>
          </div>

          <div className="row">
            {/* Recent Applications */}
            <div className="col-lg-8 col-md-12 mb-4">
              <div className="modern-hr-widget">
                <div className="modern-hr-widget-header">
                  <div className="modern-hr-widget-title">
                    <i className="bx bx-time"></i>
                    <h4>Son Başvurular</h4>
                  </div>
                  <p className="modern-hr-widget-subtitle">Son 7 günün başvuruları</p>
                </div>
                <div className="table-responsive">
                  <table className="modern-hr-table">
                    <thead>
                      <tr>
                        <th>Çalışan</th>
                        <th>Başvuru Türü</th>
                        <th>Tarih</th>
                        <th>Durum</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hrData.recentApplications.length > 0 ? (
                        hrData.recentApplications.map((app) => (
                          <tr key={app.id}>
                            <td>
                              <div>
                                <strong>{app.employee}</strong>
                                <br />
                                <small style={{color: '#64748b'}}>{app.department}</small>
                              </div>
                            </td>
                            <td>{app.type}</td>
                            <td>{app.date}</td>
                            <td>
                              <span className={`modern-hr-badge ${app.status === 'Beklemede' ? 'pending' : app.status === 'Onaylandı' ? 'approved' : 'rejected'}`}>
                                {app.status}
                              </span>
                            </td>
                                      <td>
                                        <div className="dropdown">
                                          <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                            İşlemler
                                          </button>
                                          <ul className="dropdown-menu">
                                            <li><button className="dropdown-item" onClick={() => handleViewApplication(app)}><i className="bx bx-show me-2"></i>Görüntüle</button></li>
                                            {app.status === 'Beklemede' && (
                                              <>
                                                <li><button className="dropdown-item" onClick={() => handleApproveFromTable(app)}><i className="bx bx-check me-2"></i>Onayla</button></li>
                                                <li><button className="dropdown-item" onClick={() => handleRejectFromTable(app)}><i className="bx bx-x me-2"></i>Reddet</button></li>
                                              </>
                                            )}
                                          </ul>
                                        </div>
                                      </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            Henüz başvuru bulunmuyor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

                      {/* Recent New Employees */}
                      <div className="col-lg-4 col-md-12 mb-4">
                        <div className="modern-hr-widget">
                          <div className="modern-hr-widget-header">
                            <div className="modern-hr-widget-title">
                              <i className="bx bx-user-plus"></i>
                              <h4>Son İşe Başlayanlar</h4>
                            </div>
                            <p className="modern-hr-widget-subtitle">Son 5 kişi</p>
                          </div>
                          <div>
                            {hrData.recentEmployees.length > 0 ? (
                              hrData.recentEmployees.map((employee, index) => (
                                <div key={index} className="modern-hr-birthday-item">
                                  <div className="modern-hr-birthday-avatar">
                                    <i className="bx bx-user-plus"></i>
                                  </div>
                                  <div className="modern-hr-birthday-content">
                                    <div className="modern-hr-birthday-name">{employee.name}</div>
                                    <div className="modern-hr-birthday-department">{employee.department}</div>
                                    <div className="modern-hr-birthday-date">{employee.startDate}</div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="modern-hr-empty-state">
                                <i className="bx bx-user-plus"></i>
                                <p>Son işe başlayan bulunmuyor</p>
                              </div>
                            )}
                          </div>
                        </div>

              {/* Quick Actions */}
              <div className="modern-hr-widget mt-4">
                <div className="modern-hr-widget-header">
                  <div className="modern-hr-widget-title">
                    <i className="bx bx-zap"></i>
                    <h4>Hızlı İşlemler</h4>
                  </div>
                </div>
                <div className="modern-hr-quick-actions">
                  <button className="modern-hr-quick-action">
                    <i className="bx bx-user-plus"></i>
                    <span>Yeni Çalışan Kaydı</span>
                  </button>
                  <button className="modern-hr-quick-action">
                    <i className="bx bx-calendar"></i>
                    <span>İzin Takip Sistemi</span>
                  </button>
                  <button className="modern-hr-quick-action">
                    <i className="bx bx-chart"></i>
                    <span>Performans Raporları</span>
                  </button>
                  <button className="modern-hr-quick-action">
                    <i className="bx bx-cog"></i>
                    <span>İK Ayarları</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

        {/* İzin Talep Formu Tab */}
        {activeTab === 'izin-talep' && (
          <div className="modern-hr-widget">
            <div className="modern-hr-widget-header">
              <div className="modern-hr-widget-title">
                <i className="bx bx-calendar"></i>
                <h4>İzin Talep Formu</h4>
              </div>
            </div>
            <IzinTalepFormu />
          </div>
        )}

                {/* Masraf Bildirim Formu Tab */}
                {activeTab === 'masraf-bildirim' && (
                  <div className="modern-hr-widget">
                    <div className="modern-hr-widget-header">
                      <div className="modern-hr-widget-title">
                        <i className="bx bx-receipt"></i>
                        <h4>Masraf Bildirim Formu</h4>
                      </div>
                    </div>
                    <MasrafBildirimFormu />
                  </div>
                )}

      </div>

      {/* Application Detail Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="modern-modal-overlay" onClick={handleCloseModal}>
          <div className="modern-application-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-application-header">
              <div className="modern-application-title">
                <i className="bx bx-file"></i>
                <h4>Başvuru Detayları</h4>
              </div>
              <button className="modern-application-close" onClick={handleCloseModal}>
                <i className="bx bx-x"></i>
              </button>
            </div>
            
            <div className="modern-application-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Çalışan</label>
                    <div className="modern-application-value">{selectedApplication.employee}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Departman</label>
                    <div className="modern-application-value">{selectedApplication.department}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Başvuru Türü</label>
                    <div className="modern-application-value">{selectedApplication.type}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Durum</label>
                    <div className="modern-application-value">
                      <span className={`modern-hr-badge ${getStatusBadge(selectedApplication.status)}`}>
                        {selectedApplication.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Başvuru Tarihi</label>
                    <div className="modern-application-value">{selectedApplication.date}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="modern-application-field">
                    <label>Kategori</label>
                    <div className="modern-application-value">
                      {selectedApplication.category === 'leave' ? 'İzin Talebi' : 'Masraf Raporu'}
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedApplication.category === 'leave' && (
                <div className="modern-application-section">
                  <h5>İzin Detayları</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="modern-application-field">
                        <label>İzin Başlangıç Tarihi</label>
                        <div className="modern-application-value">15 Ocak 2025</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="modern-application-field">
                        <label>İzin Bitiş Tarihi</label>
                        <div className="modern-application-value">20 Ocak 2025</div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="modern-application-field">
                        <label>İzin Sebebi</label>
                        <div className="modern-application-value">
                          Aile ziyareti için yıllık izin talebi. Önceden planlanmış bir aile buluşması için izin talep ediyorum.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedApplication.category === 'expense' && (
                <div className="modern-application-section">
                  <h5>Masraf Detayları</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="modern-application-field">
                        <label>Masraf Tutarı</label>
                        <div className="modern-application-value">₺1,250.00</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="modern-application-field">
                        <label>Masraf Kategorisi</label>
                        <div className="modern-application-value">Seyahat</div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="modern-application-field">
                        <label>Masraf Açıklaması</label>
                        <div className="modern-application-value">
                          İstanbul'dan Ankara'ya iş seyahati masrafları. Müşteri toplantısı için gerekli ulaşım ve konaklama masrafları.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modern-application-footer">
              <button className="btn btn-outline-secondary" onClick={handleCloseModal}>
                <i className="bx bx-x me-2"></i>Kapat
              </button>
              {selectedApplication.status === 'Beklemede' && (
                <>
                  <button className="btn btn-success" onClick={handleApproveApplication}>
                    <i className="bx bx-check me-2"></i>Onayla
                  </button>
                  <button className="btn btn-danger" onClick={handleRejectApplication}>
                    <i className="bx bx-x me-2"></i>Reddet
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modern-modal-overlay" onClick={handleCancelReject}>
          <div className="modern-reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-reject-header">
              <div className="modern-reject-title">
                <i className="bx bx-x-circle"></i>
                <h4>Başvuruyu Reddet</h4>
              </div>
              <button className="modern-reject-close" onClick={handleCancelReject}>
                <i className="bx bx-x"></i>
              </button>
            </div>
            
            <div className="modern-reject-body">
              <div className="modern-reject-info">
                <p><strong>Çalışan:</strong> {rejectingApplication?.employee}</p>
                <p><strong>Başvuru Türü:</strong> {rejectingApplication?.type}</p>
                <p><strong>Departman:</strong> {rejectingApplication?.department}</p>
              </div>
              
              <div className="modern-reject-form">
                <label htmlFor="rejectReason" className="modern-reject-label">
                  Red Sebebi <span className="text-danger">*</span>
                </label>
                <textarea
                  id="rejectReason"
                  className="modern-reject-textarea"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Red sebebini detaylı olarak açıklayınız..."
                  rows={4}
                />
                <small className="modern-reject-help">
                  Red sebebi çalışana bildirilecektir. Lütfen açıklayıcı bir sebep giriniz.
                </small>
              </div>
            </div>
            
            <div className="modern-reject-footer">
              <button className="btn btn-outline-secondary" onClick={handleCancelReject}>
                <i className="bx bx-x me-2"></i>İptal
              </button>
              <button className="btn btn-danger" onClick={handleConfirmReject}>
                <i className="bx bx-x me-2"></i>Reddet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default HRPage; 